import { useEffect, useState } from "react";
import { T } from "@/components/T";
import { normalizeSrc } from "@/lib/images";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import ShareButton from "@/components/ShareButton";
import { fetchEvent, rsvpEvent } from "@/lib/api";
import { eventTypeMeta, formatEventDate } from "@/lib/events";
import { ArrowLeft, MapPin, Globe, CalendarDays, Loader2, Phone, ExternalLink, Check, Users } from "lucide-react";

export default function EventDetail() {
  const { id } = useParams();
  const { pick } = useLang();
  const en = pick("", "x") === "x";
  const { user } = useAuth();
  const navigate = useNavigate();
  const [e, setE] = useState(null);
  const [err, setErr] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [rsvped, setRsvped] = useState(false);
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(0);

  useEffect(() => {
    fetchEvent(id).then((d) => { setE(d); setCount(d.rsvp_count || 0); }).catch(() => setErr(true));
  }, [id]);

  useEffect(() => { if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" })); }, [user]);

  if (err) return <div className="mx-auto max-w-2xl px-5 py-24 text-center text-ink-2">{pick("यह आयोजन नहीं मिला।", "This event could not be found.")}</div>;
  if (!e) return <div className="flex items-center gap-2 px-5 py-24 text-ink-3"><Loader2 className="h-4 w-4 animate-spin" /> <T>Loading…</T></div>;

  const tm = eventTypeMeta(e.type);
  const submitRsvp = async (ev) => {
    ev.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      const r = await rsvpEvent(id, form);
      setRsvped(true); setCount(r.rsvp_count || count + 1);
      toast(pick("पंजीकरण हो गया", "You're registered"), { description: r.already ? pick("आप पहले से पंजीकृत हैं।", "You had already registered.") : e.title });
    } catch (er) { toast(pick("नहीं हो सका", "Could not RSVP"), { description: er?.response?.data?.detail || pick("पुनः प्रयास करें।", "Try again.") }); }
    finally { setBusy(false); }
  };

  return (
    <article data-testid="event-detail-page" className="mx-auto max-w-3xl px-5 py-10 md:px-8">
      <Seo path={`/events/${id}`} title={`${e.title} — Event on Dharmartha`} description={e.description || "An event on Dharmartha."} />
      <Link to="/events" className="inline-flex items-center gap-1.5 text-sm text-ink-2 transition-colors hover:text-saffron"><ArrowLeft className="h-4 w-4" /> {pick("सभी आयोजन", "All events")}</Link>

      {e.image_url && (
        <div className="mt-5 overflow-hidden border border-line">
          <img src={normalizeSrc(e.image_url)} alt="" className="max-h-80 w-full object-contain" />
        </div>
      )}

      <div className="mt-6 flex items-center gap-2">
        <span className="rounded-sm px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-white" style={{ backgroundColor: tm.accent }}>{pick(tm.deva, tm.en)}</span>
        <span className="text-xs text-gold">{e.host === "dharmartha" ? pick("धर्मार्थ द्वारा", "Hosted by Dharmartha") : (e.organizer_name || pick("समुदाय द्वारा", "By the community"))}</span>
      </div>
      <h1 className="mt-3 font-serif-latin text-3xl text-ink md:text-4xl">{e.title}</h1>

      <div className="mt-4 space-y-2 text-sm text-ink-2">
        <p className="inline-flex items-center gap-2"><CalendarDays className="h-4 w-4 text-saffron" /> {formatEventDate(e.start, en)}{e.end ? ` – ${formatEventDate(e.end, en)}` : ""}</p>
        {e.mode !== "online" && (e.venue || e.city) && <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-saffron" /> {[e.venue, e.city].filter(Boolean).join(", ")}</p>}
        {e.mode !== "in_person" && e.online_url && <p className="flex items-center gap-2"><Globe className="h-4 w-4 text-saffron" /> <a href={e.online_url} target="_blank" rel="noreferrer" className="text-saffron underline">{pick("ऑनलाइन लिंक", "Join online")}</a></p>}
        {e.contact && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-saffron" /> {e.contact}</p>}
        {count > 0 && <p className="flex items-center gap-2 text-ink-3"><Users className="h-4 w-4" /> {count} {pick("पंजीकृत", "registered")}</p>}
      </div>

      {e.description && <p className="mt-6 whitespace-pre-wrap leading-relaxed text-ink-2">{e.description}</p>}

      <div className="mt-8 flex flex-wrap items-center gap-3">
        {e.register_url && (
          <a href={e.register_url} target="_blank" rel="noreferrer" data-testid="event-register-link"
            className="inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
            {pick("पंजीकरण करें", "Register / Join")} <ExternalLink className="h-4 w-4" />
          </a>
        )}
        <ShareButton label={pick("आयोजन साझा करें", "Share event")} testid="event-share" />
      </div>

      {/* On-site RSVP */}
      {e.rsvp_enabled && (
        <div className="mt-8 border border-line bg-surface p-5" data-testid="event-rsvp">
          <h2 className="font-serif-latin text-lg text-ink">{pick("इस आयोजन के लिए पंजीकरण करें", "RSVP for this event")}</h2>
          {rsvped ? (
            <p className="mt-3 inline-flex items-center gap-2 text-sm text-green-700"><Check className="h-4 w-4" /> {pick("धन्यवाद! आपका पंजीकरण दर्ज हो गया।", "Thank you! Your RSVP is recorded.")}</p>
          ) : (
            <form onSubmit={submitRsvp} className="mt-3 grid gap-3 sm:grid-cols-3">
              <input value={form.name} onChange={(ev) => setForm({ ...form, name: ev.target.value })} data-testid="rsvp-name" placeholder={pick("आपका नाम", "Your name")} className="border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
              <input value={form.email} onChange={(ev) => setForm({ ...form, email: ev.target.value })} data-testid="rsvp-email" placeholder={pick("ईमेल (वैकल्पिक)", "Email (optional)")} className="border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
              <input value={form.phone} onChange={(ev) => setForm({ ...form, phone: ev.target.value })} data-testid="rsvp-phone" placeholder={pick("मोबाइल (वैकल्पिक)", "Mobile (optional)")} className="border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
              <button type="submit" disabled={busy} data-testid="rsvp-submit" className="inline-flex items-center justify-center gap-1.5 bg-[var(--ink)] px-4 py-2 text-sm font-medium text-[var(--surface)] disabled:opacity-60 sm:col-span-1">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {pick("पंजीकरण", "RSVP")}
              </button>
            </form>
          )}
        </div>
      )}
    </article>
  );
}
